(ns app.front.ui.label-group
  (:require [app.domain.label.model :as label-model]
            [app.front.ui :as ui]
            [app.front.ui.icon-button :as icon-button]
            [app.front.util :as futil]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.icons :as i]
            [potpuri.core :as p]))

(defn name-conflict
  "Shared duplicate-name check for label/group name inputs.

   Given the current input `value` and `existing-names` (a map name->id, a set, or a seq of
   names), returns a map {:conflict? bool :can-save? bool} using the shared
   `label-model/conflict-new-name-valid?` predicate.

   When `existing-names` is a name->id map and `current-id` is provided, the entity with that
   id is excluded from the check so an entity's own (pre-filled) name is not treated as a
   duplicate with itself. When `existing-names` is absent/empty, conflict checking is disabled
   and only a non-blank value is required to save."
  [{:keys [value existing-names current-id]}]
  (let [others    (if (and (some? current-id) (map? existing-names))
                    (into {} (remove (fn [[_ id]] (= id current-id))) existing-names)
                    existing-names)
        check?    (boolean (seq others))
        valid?    (label-model/conflict-new-name-valid? value others)
        nonblank? (seq (str/trim (or value "")))]
    {:conflict? (boolean (and check? nonblank? (not valid?)))
     :can-save? (boolean (and nonblank? (or (not check?) valid?)))}))

(defn label-chip
  "Individual label displayed as a chip/pill. Click to edit.

   Options:
   - :tr           - translation function
   - :label        - label map with :id and :name
   - :editing?     - whether this label is being edited
   - :editing-name - current editing name value
   - :on-click     - callback when chip is clicked (to start editing)
   - :on-change    - callback when editing input changes, receives new value
   - :on-save      - callback when save is triggered (Enter or button)
   - :on-cancel    - callback when editing is cancelled (Escape)
   - :on-delete    - callback when delete is triggered, receives label-id
   - :existing-names - (optional) map (name->id) / set / seq of sibling label names; when provided,
                       saving is blocked while `editing-name` duplicates one of them
   - :current-id     - (optional) id of the label being edited; excluded from the conflict check
                       (see `name-conflict`)
   - :conflict-text  - (optional) message shown when the name conflicts, defaults to the
                       label-name-taken translation"
  [{:keys [tr label editing? editing-name on-click on-change on-save on-cancel on-delete
           existing-names current-id conflict-text]}]
  (if editing?
    (let [{:keys [conflict? can-save?]} (name-conflict {:value          editing-name
                                                        :existing-names existing-names
                                                        :current-id     current-id})
          save!                         #(when (and on-save can-save?) (on-save))]
      [:div.inline-flex.items-center.gap-1.m-1
       [:input.rounded.px-2.py-1.text-sm.outline-none.border.border-solid
        {:type        "text"
         :id          (:id label)
         :name        "label-name"
         :value       editing-name
         :auto-focus  true
         :class       (if conflict? "border-red-500" "border-gray-400")
         :title       (when conflict? (or conflict-text (tr [:labels/label-name-taken])))
         :on-change   #(when on-change (on-change (futil/tval %)))
         :on-key-down #(case (.-key %)
                         "Enter"  (save!)
                         "Escape" (when on-cancel (on-cancel))
                         nil)}]
       (cc/button {:kind             :ghost
                   :size             :small
                   :icon             i/check
                   :icon-description (tr [:save])
                   :disabled         (not can-save?)
                   :class-name       "only-icon-button-base !p-1 min-h-0 border border-purple-600 text-purple-600 rounded hover:bg-purple-50"
                   :on-click         save!}
                  "")
       (cc/button {:kind             :ghost
                   :size             :small
                   :icon             i/trash
                   :icon-description (tr [:delete])
                   :class-name       "only-icon-button-base !p-1 min-h-0 border border-purple-600 text-purple-600 rounded hover:bg-purple-50"
                   :on-click         #(when on-delete (on-delete (:id label)))}
                  "")])
    (cc/button {:kind       :ghost
                :size       :small
                :class-name "m-1 rounded border border-gray-300 hover:bg-gray-50 normal-case !px-3"
                :on-click   #(when on-click (on-click))}
               (:name label))))

(defn label-group-header
  "Header for a label group with expand/collapse, edit and delete icons when expanded.

   Options:
   - :tr          - translation function
   - :group       - group map with :id and :name
   - :label-count - number of labels in the group
   - :expanded?   - whether the group is expanded
   - :on-toggle   - callback when header is clicked to toggle
   - :on-edit     - callback when edit icon is clicked
   - :on-delete   - callback when delete icon is clicked"
  [{:keys [tr group label-count expanded? on-toggle on-edit on-delete]}]
  [:div.flex.items-center.justify-between.h-9.px-2.py-2.cursor-pointer.bg-gray-100.border.border-gray-300.rounded
   {:role          "button"
    :tab-index     0
    :aria-expanded expanded?
    :on-click      on-toggle
    :on-key-down   #(when (contains? #{"Enter" " "} (.-key %))
                      (.preventDefault %)
                      (on-toggle))}
   [:div.flex.items-center
    [cc/icon {:icon        (if expanded? i/chevron-down i/chevron-right)
              :description (tr (if expanded? [:carbon.table.row.collapse] [:carbon.table.row.expand]))
              :width       "14"
              :height      "14"}]
    [:span.text-base.ml-2 (:name group)]
    [:span.text-gray-500.ml-1 (str "(" label-count ")")]]
   (when expanded?
     [:div.flex.items-center.gap-1
      {:on-click #(.stopPropagation %)}
      (cc/button (p/assoc-if {:kind             :ghost
                              :size             :small
                              :icon             i/pen
                              :icon-description (tr [:labels/edit-group-name])
                              :class-name       "only-icon-button-base !p-1 min-h-0 hover:text-purple-600"}
                             :on-click          on-edit)
                 "")
      (cc/button (p/assoc-if {:kind             :ghost
                              :size             :small
                              :icon             i/trash
                              :icon-description (tr [:labels/delete-group])
                              :class-name       "only-icon-button-base !p-1 min-h-0 hover:text-red-500"}
                             :on-click          on-delete)
                 "")])])

(defn new-label-input
  "Input field for adding a new label to a group.

   Options:
   - :id             - unique id for the input field
   - :tr             - translation function
   - :placeholder    - (optional) placeholder text, defaults to new-label-name translation
   - :value          - current input value
   - :show-buttons?  - whether to show save/cancel buttons
   - :save-disabled? - whether save action is disabled
   - :on-change      - callback when input changes, receives new value
   - :on-focus       - callback when input gains focus
   - :on-blur        - callback when input loses focus
   - :on-save        - callback when save is triggered
   - :on-cancel      - callback when cancel is triggered
   - :existing-names - (optional) map/set/seq of existing names; when provided, saving is blocked
                       (and an inline error shown) if `value` duplicates one of them
   - :conflict-text  - (optional) message shown on conflict, defaults to label-name-taken"
  [{:keys [id tr placeholder value show-buttons? save-disabled? on-change on-focus on-blur on-save on-cancel
           existing-names conflict-text]}]
  (let [{:keys [conflict? can-save?]} (name-conflict {:value value :existing-names existing-names})
        disabled?                     (or (true? save-disabled?)
                                          (not can-save?))]
    [:div.flex.items-start.gap-2.mt-3
     [:div.relative.flex-1
      [ui/text-input
       {:id           id
        :placeholder  (or placeholder (tr [:labels/new-label-name]))
        :value        value
        :hide-label   true
        :input-class  "pr-8 border-b"
        :invalid      conflict?
        :invalid-text (when conflict? (or conflict-text (tr [:labels/label-name-taken])))
        :on-focus     on-focus
        :on-blur      on-blur
        :on-change    #(when on-change (on-change (futil/tval %)))}]
      [:span {:class       "mt-2 pointer-events-none absolute right-2 top-1 -translate-y-1/2 text-gray-400"
              :aria-hidden true}
       [cc/icon {:icon        i/pen
                 :description ""
                 :width       "14"
                 :height      "14"}]]]
     [:div.flex.gap-2.mt-1.transition-all.duration-300.ease-in-out.overflow-hidden
      {:class (if show-buttons? "opacity-100 max-w-xs" "opacity-0 max-w-0")}
      [icon-button/icon-button
       {:icon        i/check
        :description (tr [:save])
        :on-click    #(when (and on-save (not disabled?)) (on-save))
        :disabled?   disabled?
        :class       "p-1 border-2 border-purple-600 text-purple-600 rounded hover:bg-purple-50 transition-colors"
        :fill        "currentColor"}]
      [icon-button/icon-button
       {:icon        i/remove
        :description (tr [:cancel])
        :on-click    on-cancel
        :class       "p-1 border-2 border-purple-600 text-purple-600 rounded hover:bg-purple-50 transition-colors"
        :fill        "currentColor"}]]]))

(defn add-group-form
  "Form for adding a new label group.
   Options:
   - :tr        - translation function
   - :value     - current input value
   - :disabled?  - whether the form is disabled
   - :on-change - callback when input changes, receives new value
   - :on-save   - callback when save is triggered
   - :on-cancel - callback when cancel is triggered
   - :existing-names - (optional) map/set/seq of existing group names; when provided, saving is
                       blocked (and an inline error shown) if `value` duplicates one of them
   - :conflict-text  - (optional) message shown on conflict, defaults to group-name-taken"
  [{:keys [tr value disabled? on-change on-save on-cancel existing-names conflict-text]}]
  (let [{:keys [conflict? can-save?]} (name-conflict {:value value :existing-names existing-names})]
    [:div.border.border-gray-300.rounded-lg.mb-4.p-4.bg-gray-50
     [:div.flex.items-center.gap-2
      [:div.flex-1
       [ui/text-input {:placeholder  (tr [:labels/group-name])
                       :value        value
                       :auto-focus   true
                       :invalid      conflict?
                       :invalid-text (when conflict? (or conflict-text (tr [:labels/group-name-taken])))
                       :on-change    #(when on-change (on-change (futil/tval %)))}]]
      (cc/button {:kind     :primary
                  :size     :small
                  :disabled (or disabled? (not can-save?))
                  :on-click #(when (and on-save can-save?) (on-save))}
                 (tr [:save]))
      (cc/button {:kind     :ghost
                  :size     :small
                  :on-click on-cancel}
                 (tr [:cancel]))]]))

(defn group-name-edit-modal
  "Modal for editing a group name.

   Options:
   - :tr                    - translation function
   - :open?                 - whether the modal is open
   - :value                 - current editing value
   - :on-change             - callback when input changes, receives new value
   - :on-save               - callback when save is triggered
   - :on-close              - callback when modal is closed
   - :heading               - (optional) custom modal heading
   - :secondary-button-text - (optional) custom secondary button text
   - :on-secondary          - (optional) callback for secondary button (if different from close)
   - :existing-names        - (optional) map (name->id) / set / seq of existing names; when provided,
                              saving is blocked (button disabled + inline error) if `value` duplicates
                              one of them (case-insensitive, trimmed)
   - :current-id            - (optional) db id of the entity being edited; when `existing-names` is a
                              name->id map, that entity's own name is excluded from the conflict check
                              so the pre-filled name does not flag as a duplicate"
  [{:keys [tr open? value on-change on-save on-close heading secondary-button-text on-secondary existing-names current-id]}]
  (let [{:keys [conflict? can-save?]} (name-conflict {:value          value
                                                      :existing-names existing-names
                                                      :current-id     current-id})]
    [cc/modal {:open                    open?
               :modal-heading           (or heading (tr [:labels/edit-group-name]))
               :primary-button-text     (tr [:save])
               :secondary-button-text   (or secondary-button-text (tr [:cancel]))
               :on-request-close        on-close
               :on-request-submit       #(when can-save? (on-save))
               :on-secondary-submit     (or on-secondary on-close)
               :primary-button-disabled (not can-save?)}
     [:div.py-4
      [ui/text-input
       {:id           "edit-group-name"
        :label-text   (tr [:labels/group-name])
        :value        value
        :auto-focus   true
        :invalid      conflict?
        :invalid-text (when conflict? (tr [:labels/group-name-taken]))
        :on-change    #(when on-change (on-change (futil/tval %)))}]]]))
